import { useState } from "react";
import PropTypes from "prop-types";

// @mui components
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Typography,
  Collapse,
  IconButton,
  useTheme,
  useMediaQuery,
  Box,
  Stack,
  Chip
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from "@mui/icons-material";

// Argon components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

function ResponsiveTable({ columns, rows, title, enableMobileCards = true }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedCards, setExpandedCards] = useState({});

  const handleCardExpand = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Mobile card view
  const MobileCardView = () => {
    return (
      <ArgonBox>
        {title && (
          <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
            {title}
          </ArgonTypography>
        )}
        <Stack spacing={2}>
          {rows.map((row, index) => {
            const primaryColumn = columns[0];
            const secondaryColumns = columns.slice(1, 3); // Show 2-3 key columns
            const remainingColumns = columns.slice(3);
            const isExpanded = expandedCards[index];

            return (
              <Card key={index} elevation={1}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {/* Primary info - always visible */}
                  <ArgonBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <ArgonBox flex={1}>
                      <ArgonTypography variant="subtitle1" fontWeight="bold" mb={0.5}>
                        {typeof row[primaryColumn.name] === 'object' 
                          ? row[primaryColumn.name]
                          : row[primaryColumn.name]
                        }
                      </ArgonTypography>
                      
                      {/* Secondary info */}
                      {secondaryColumns.map((column) => (
                        <ArgonBox key={column.name} display="flex" alignItems="center" mb={0.5}>
                          <ArgonTypography variant="caption" color="text" mr={1} minWidth="80px">
                            {column.name.charAt(0).toUpperCase() + column.name.slice(1)}:
                          </ArgonTypography>
                          <ArgonBox>
                            {typeof row[column.name] === 'object' 
                              ? row[column.name]
                              : (
                                <ArgonTypography variant="body2" fontWeight="medium">
                                  {row[column.name]}
                                </ArgonTypography>
                              )
                            }
                          </ArgonBox>
                        </ArgonBox>
                      ))}
                    </ArgonBox>

                    {/* Expand button */}
                    {remainingColumns.length > 0 && (
                      <IconButton
                        size="small"
                        onClick={() => handleCardExpand(index)}
                        sx={{ ml: 1 }}
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    )}
                  </ArgonBox>

                  {/* Expanded content */}
                  {remainingColumns.length > 0 && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <ArgonBox pt={2} borderTop="1px solid #eee">
                        {remainingColumns.map((column) => (
                          <ArgonBox key={column.name} display="flex" alignItems="center" mb={1}>
                            <ArgonTypography variant="caption" color="text" mr={1} minWidth="100px">
                              {column.name.charAt(0).toUpperCase() + column.name.slice(1)}:
                            </ArgonTypography>
                            <ArgonBox>
                              {typeof row[column.name] === 'object' 
                                ? row[column.name]
                                : (
                                  <ArgonTypography variant="body2" fontWeight="medium">
                                    {row[column.name]}
                                  </ArgonTypography>
                                )
                              }
                            </ArgonBox>
                          </ArgonBox>
                        ))}
                      </ArgonBox>
                    </Collapse>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </ArgonBox>
    );
  };

  // Desktop table view
  const DesktopTableView = () => {
    return (
      <TableContainer component={Paper} elevation={0}>
        {title && (
          <ArgonBox p={3} pb={0}>
            <ArgonTypography variant="h6" fontWeight="medium">
              {title}
            </ArgonTypography>
          </ArgonBox>
        )}
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.name}
                  align={column.align || "left"}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: theme.palette.text.secondary,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    py: 2
                  }}
                >
                  {column.name.charAt(0).toUpperCase() + column.name.slice(1)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                  '&:last-child td': {
                    border: 0,
                  },
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.name}
                    align={column.align || "left"}
                    sx={{ py: 2 }}
                  >
                    {row[column.name]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <ArgonBox>
      {isMobile && enableMobileCards ? <MobileCardView /> : <DesktopTableView />}
    </ArgonBox>
  );
}

ResponsiveTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      align: PropTypes.oneOf(["left", "center", "right"]),
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string,
  enableMobileCards: PropTypes.bool,
};

ResponsiveTable.defaultProps = {
  title: null,
  enableMobileCards: true,
};

export default ResponsiveTable;